import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../hooks/useApi';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import UserAvatar from '../../components/common/UserAvatar';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Clock, MessageCircle, Star, CheckCircle, Truck,
  Wrench, User, FileText, Calendar, DollarSign, Shield, Phone, Mail,
  Send, AlertTriangle, ExternalLink, Copy
} from 'lucide-react';
import UnifiedMap from '../../components/common/UnifiedMap';

const BACKEND_URL = window.location.origin;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  on_the_way: { label: 'On the Way', color: 'bg-purple-100 text-purple-800', icon: Truck },
  arrived: { label: 'Arrived', color: 'bg-indigo-100 text-indigo-800', icon: MapPin },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-800', icon: Wrench },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
};

export default function AdminBookingDetail() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDetails = useCallback(async () => {
    try {
      const res = await api.getAdminBookingDetails(bookingId);
      setDetails(res.data);
    } catch (error) {
      console.error('Failed to fetch booking details:', error);
      toast.error('Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleCopyShareLink = async () => {
    try {
      const res = await api.createShareLink(bookingId);
      const shareUrl = `${window.location.origin}/track/${res.data.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (e) {
      toast.error('Failed to create share link');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        <AdminSidebar />
      <AdminMobileNav />
        <div className="flex-1 flex items-center justify-center md:ml-64 pt-16 md:pt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        <AdminSidebar />
      <AdminMobileNav />
        <div className="flex-1 flex items-center justify-center md:ml-64 pt-16 md:pt-4">
          <p className="text-slate-500">Booking not found</p>
        </div>
      </div>
    );
  }

  const { booking, customer, provider, service, category, timeline, review, chat_messages, provider_location } = details;
  const status = statusConfig[booking.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const hasMap = booking.address?.latitude && booking.address?.longitude;

  const UserCard = ({ user: u, label, role, address }) => (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid={`${role}-card`}>
      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">{label}</h4>
      {u ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-stone-100 overflow-hidden flex items-center justify-center">
              <UserAvatar src={u.profile_image} name={u.name} role={role} size="lg" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">{u.name}</p>
                {u.face_verified && <Shield className="w-4 h-4 text-green-500" />}
              </div>
              {u.rating > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span className="text-xs text-slate-600">{u.rating} ({u.total_jobs} jobs)</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1 text-sm">
            {u.email && (
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-3.5 h-3.5" /> {u.email}
              </div>
            )}
            {u.phone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-3.5 h-3.5" /> {u.phone}
              </div>
            )}
            {address && (
              <div className="mt-2 p-2 bg-stone-50 rounded-lg">
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs">{address.address || address.full_address}</p>
                    {(address.barangay || address.city || address.province) && (
                      <p className="text-xs text-slate-400">
                        {[address.barangay, address.city, address.province].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400">Not assigned</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/bookings')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">
                Booking #{booking.id.slice(0, 8).toUpperCase()}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {status.label}
                </span>
                <span className="text-sm text-slate-500">
                  Created {new Date(booking.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyShareLink}
            data-testid="share-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Share Link
          </Button>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <UserCard user={customer} label="Customer" role="customer" address={customer?.default_address || booking.address} />
          <UserCard user={provider} label="Provider" role="provider" address={provider?.default_address} />
          <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="payment-card">
            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">Payment</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Base Price</span>
                <span className="text-slate-900 font-medium">Php {booking.base_price?.toFixed(2)}</span>
              </div>
              {booking.add_ons_price > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Add-ons</span>
                  <span className="text-slate-900">Php {booking.add_ons_price?.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between">
                <span className="font-semibold text-slate-900">Total</span>
                <span className="font-bold text-lg text-amber-600">Php {booking.total_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Commission ({(booking.commission_rate * 100).toFixed(0)}%)</span>
                <span className="text-green-600 font-medium">Php {booking.commission_amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Provider Earnings</span>
                <span className="text-slate-700">Php {booking.provider_earnings?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline ({timeline.length})</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">Chat ({chat_messages.length})</TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map">Map</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Service Info */}
              <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="service-info">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">Service Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Wrench className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{service?.name || 'N/A'}</p>
                      <p className="text-xs text-slate-500">{category?.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{booking.scheduled_date}</p>
                      <p className="text-xs text-slate-500">{booking.scheduled_time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{booking.address?.full_address}</p>
                      {(booking.address?.barangay || booking.address?.city || booking.address?.province) && (
                        <p className="text-xs text-slate-500">
                          {[booking.address?.barangay, booking.address?.city, booking.address?.province].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {booking.address?.landmark && (
                        <p className="text-xs text-slate-400">Landmark: {booking.address?.landmark}</p>
                      )}
                    </div>
                  </div>
                  {booking.notes && (
                    <div className="bg-stone-50 rounded-lg p-3 mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1">Notes</p>
                      <p className="text-sm text-slate-700">{booking.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Review */}
              <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="review-section">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">Review</h4>
                {review ? (
                  <div>
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`w-5 h-5 ${s <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                      ))}
                      <span className="ml-2 font-semibold text-slate-900">{review.rating}/5</span>
                    </div>
                    {review.comment && <p className="text-sm text-slate-600 mt-2">{review.comment}</p>}
                    <p className="text-xs text-slate-400 mt-2">{new Date(review.created_at).toLocaleString()}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No review submitted yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="timeline-section">
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No timeline data</p>
              ) : (
                <div className="space-y-0">
                  {timeline.map((entry, idx) => {
                    const step = statusConfig[entry.status];
                    const StepIcon = step?.icon || Clock;
                    const isLast = idx === timeline.length - 1;
                    return (
                      <div key={entry.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLast ? 'bg-slate-900' : 'bg-stone-100'}`}>
                            <StepIcon className={`w-5 h-5 ${isLast ? 'text-white' : 'text-slate-500'}`} />
                          </div>
                          {idx < timeline.length - 1 && <div className="w-0.5 h-10 bg-stone-200" />}
                        </div>
                        <div className="pb-6">
                          <p className="font-medium text-slate-900">{entry.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                          <p className="text-sm text-slate-500">{new Date(entry.created_at).toLocaleString()}</p>
                          {entry.notes && <p className="text-sm text-slate-600 mt-1 bg-stone-50 px-3 py-1 rounded">{entry.notes}</p>}
                          {entry.latitude && (
                            <p className="text-xs text-slate-400 mt-1">Location: {entry.latitude.toFixed(4)}, {entry.longitude.toFixed(4)}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="chat-section">
              <div className="px-5 py-3 border-b bg-stone-50">
                <p className="text-sm font-medium text-slate-700">{chat_messages.length} message{chat_messages.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {chat_messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">No chat messages for this booking</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {chat_messages.map(msg => {
                      const isCustomer = msg.sender_id === booking.customer_id;
                      const senderName = isCustomer ? customer?.name : provider?.name;
                      return (
                        <div key={msg.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[70%] ${isCustomer ? 'bg-stone-100' : 'bg-slate-900 text-white'} rounded-2xl px-4 py-2.5`}>
                            <p className={`text-xs font-medium mb-1 ${isCustomer ? 'text-slate-500' : 'text-slate-300'}`}>
                              {senderName} ({msg.sender_role})
                            </p>
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isCustomer ? 'text-slate-400' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="map-section">
              {hasMap ? (
                <UnifiedMap
                center={[booking.address.latitude, booking.address.longitude]}
                zoom={14}
                height="400px"
                markers={[
                  { lat: booking.address.latitude, lng: booking.address.longitude, color: 'red', label: `Service Address: ${booking.address.full_address}` },
                  ...(provider_location ? [{ lat: provider_location.latitude, lng: provider_location.longitude, color: 'blue', label: 'Provider Location' }] : [])
                ]}
              />
              ) : (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No location data for this booking</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
